/* @pareto-engineering/generator-front 1.1.1-alpha.1 */
import * as React from 'react'
import { useLazyLoadQuery } from 'react-relay'
import { useCases } from 'modules/site/pages/content/Home'
import { SVG, useTheme, Page } from '@pareto-engineering/design-system'
import {
  RelatedPosts,
  SolutionCard,
  NavigationBar,
  ConversionPage,
  aiSectionMap,
} from 'modules/components'
import styleNames from '@pareto-engineering/bem/exports'
import Loading from 'utils/Loading'
import PropTypes from 'prop-types'

const SolutionSuggestions = () => {
  const aggregatedUseCases = useCases
    .filter((useCase) => useCase.path !== '')
    .sort(() => 0.5 - Math.random())
  const useCaseExamples = aggregatedUseCases.slice(0, 4)

  return (
    <div className="grid span-grid">
      <SolutionCard.Group
        className="span-8 md-span-8"
        type="snap-scroller"
        desktopType="grid"
      >
        {useCaseExamples.map((useCase) => (
          <SolutionCard
            className="span-7 md-span-4 manual-mobile-only"
            key={useCase.path}
            arrowPosition="top"
            style={{
              height   :'100%',
              minHeight:'170px',
            }}
            {...useCase}
          />
        ))}
      </SolutionCard.Group>
    </div>
  )
}

const BlogSuggestions = () => {
  const BLOG_404_QUERY = graphql`
    query Page404BlogPostQuery {
      publicBlogPosts(last: 4) {
        edges {
          node {
            ...ArticleCardFragment
          }
        }
      }
    }
  `

  const data = useLazyLoadQuery(BLOG_404_QUERY, {})
  const suggestedPosts = data.publicBlogPosts?.edges?.map(({ node }) => node)

  return (
    <RelatedPosts items={suggestedPosts} className="s-1 md-s-2" />
  )
}

/**
 * Default template for 404 page
 *
 * Parameters include:
 * the error message
 * the suggested content type
 * the static context (to return 404 status code)
 */
const Page404 = ({ message, type, staticContext }) => {
  const baseClassName = styleNames.base
  const componentClassName = 'page-404'

  if (staticContext) {
    staticContext.status = 404
  }

  const helmetProps = {
    title           :'Oops...',
    meta_description:"We couldn't find the solution you were looking for.",
    canonical       :'/*',
    og_type         :'website',
  }

  const cta = type === 'blog' ? 'Check out these articles below:' : 'Check out these solutions below:'

  const { userTheme } = useTheme()
  const imageData = {
    sprite:'/404-image.svg',
    target:userTheme,
  }

  return (
    <ConversionPage
      conversionFormConfig={aiSectionMap}
      mobileButtonText="Get started"
      desktopLaunchText="Join leading AI companies in harnessing world-class AI talent."
      mobileLaunchText="Harness world-class AI talent."
      id="page-page404"
      helmetProps={helmetProps}
      className={[baseClassName, componentClassName, 'x-background-far b-x']
        .filter((e) => e)
        .join(' ')}
      style={{ minHeight: '100vh' }}
    >
      <NavigationBar />
      <Page.Section id="body" isSpaced>
        <div
          className="span-8 md-span-2 md-start-2"
          style={{ paddingTop: '3em', marginBottom: '-2em' }}
        >
          <SVG
            sprite={imageData.sprite}
            target={imageData.target}
            viewBox="0 0 83 83"
            width="83"
          />
        </div>
        <div className="span-8 md-span-10 md-start-4" style={{ paddingTop: '2em' }}>
          <p>
            { message }
          </p>
          <p className="x-main c-x">
            { cta }
          </p>
          <div style={{ marginTop: '2em' }}>
            {type === 'blog' && (
              <BlogSuggestions />
            )}
            {type !== 'blog' && (
              <SolutionSuggestions />
            )}
          </div>
        </div>
      </Page.Section>
    </ConversionPage>
  )
}

Page404.propTypes = {
  /**
   * The message that will be displayed
   */
  message:PropTypes.string,

  /**
   * The type of 404 Page (either blog or solution)
   */
  type:PropTypes.string,

}

Page404.defaultProps = {
  message:"We're sorry, but it looks like the data you're searching for was not found.",
  type   :'solution',
}

export default (props) => (
  <React.Suspense fallback={<Loading />}>
    <Page404 {...props} />
  </React.Suspense>
)
